import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";

export interface RecentTask {
  project_id: string;
  description: string | null;
  completed_at: string;
}

/**
 * Fetch the most recently completed/approved tasks across all active projects.
 * Returns a Map: project_id → last 3 completed task descriptions.
 */
export function useRecentCompletedTasks() {
  return useQuery({
    queryKey: ["recent-completed-tasks"],
    queryFn: async (): Promise<Map<string, RecentTask[]>> => {
      // Hent ALLE fullførte — .limit(200) droppet allerede 10/47 prosjekter
      // fra tooltipen, og team-brede assignments vokser mot 1000-rad-taket.
      // fetchAllPaginated pager på id; nyest-først sorteres client-side. VIB-1249.
      const data = await fetchAllPaginated<{
        completed_at: string;
        budget_lines: {
          description: string | null;
          project_id: string | null;
          projects: { deleted_at: string | null; is_active: boolean | null } | null;
        } | null;
      }>(
        "budget_line_assignments",
        `
          completed_at,
          budget_lines!inner(
            description,
            project_id,
            projects!inner(deleted_at, is_active)
          )
        `,
        (q) =>
          q
            .in("task_status", ["completed", "approved"])
            .not("completed_at", "is", null),
      );

      data.sort((a, b) => b.completed_at.localeCompare(a.completed_at));

      const byProject = new Map<string, RecentTask[]>();

      for (const row of data) {
        const bl = row.budget_lines;
        const proj = bl?.projects;
        if (!proj || !proj.is_active || proj.deleted_at) continue;

        const pid = bl.project_id as string;
        const existing = byProject.get(pid) ?? [];
        if (existing.length >= 3) continue;

        existing.push({
          project_id: pid,
          description: bl.description,
          completed_at: row.completed_at,
        });
        byProject.set(pid, existing);
      }

      return byProject;
    },
    staleTime: 2 * 60 * 1000,
  });
}
